<script>
  import { onMount } from "svelte";
  import { createChart } from "./uplot/createChart.js"; 
  import "./uplot/uPlot.min.css";

  export let border = true;
  export let title = "";
  export let data;

  $: setData(uplot, data);

  let chart, uplot, plot;
  
  let chartData;

  let canPan = false;
  let isPanning = false;

  let xMin, xMax, xRange;

  // For mouse events
  let left0, scXMin0, scXMax0, xUnitsPerPx;

  // For touch event
  let rafPending = false;
  let rect, oxRange, xVal;
  let from = { x: 0, dx: 0 };
  let to = { x: 0, dx: 0 };

  function setData(uplot, data) {
    if (!uplot || !data) return;    

    xMin = data.time[0];
    xMax = data.time[data.time.length - 1];
    xRange = xMax - xMin;
    chartData = data;
    refreshData(uplot);
    autoScaleY();
  }

  function refreshData(uplot) {
    uplot.setData([chartData.time, chartData.total, chartData.good, chartData.bad]);
  }

  let pointerEvents = [];
  let touch = false;
  function onPointerDown(e) {
    left0 = e.clientX;
    scXMin0 = uplot.scales.x.min;
    scXMax0 = uplot.scales.x.max;
    xUnitsPerPx = uplot.posToVal(1, "x") - uplot.posToVal(0, "x");

    let axis = findAxis(e.x, e.y);
    isPanning = e.button === 1 || (e.button === 0 && axis?.scale === "x");

    pointerEvents.push(e);
    
    if (e.pointerType === "touch") {
      rect = uplot.over.getBoundingClientRect();
      storePos(from);
      oxRange = uplot.scales.x.max - uplot.scales.x.min;
      let left = from.x;
      xVal = uplot.posToVal(left, "x");
    }

    if (isPanning) e.preventDefault();
  }

  function onDblClick(e) {
    let axis = findAxis(e.x, e.y);
    if (axis) {
      if (axis.scale === "x") {
        const range = { min: xMin, max: xMax};
        uplot.setScale("x", range);
      } else {
        autoScaleY();
      }
    }
  }

  function autoScaleY() {
    let max = findMax(chartData.total);
    max = Math.max(max, findMax(chartData.good));
    max = Math.max(max, findMax(chartData.bad));
    uplot.setScale("y", { min: 0, max });
  }

  function findMax(array) {
    let max = 0;
    for (let value of array) {
      max = Math.max(value, max);
    }
    return max;
  }

  function onPointerMove(e) {
    canPan = e.y > plot.getBoundingClientRect().bottom;
    if (isPanning) {
      let left1 = e.clientX;
      let dx = xUnitsPerPx * (left1 - left0);

      if (uplot.scales.x.min !== (scXMin0 - dx)) {
        const range = { min: Math.max(xMin, scXMin0 - dx), max: Math.min(xMax, scXMax0 - dx) };
        uplot.setScale("x", range);
      }
    } else if (touch && pointerEvents.length === 2) {
      storePos(to);

      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(zoom);
      }
    }
  }

  function inRect(rect, x, y) {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }

  function expandRect(rect, left, right, top, bottom) {
    const x = rect.left - left;
    const y = rect.top - top;
    const width = (rect.right + right) - x;
    const height = (rect.bottom + bottom) - y;
    return new DOMRect(x, y, width, height);
  }

  function onPointerUp() {
    isPanning = false;
    pointerEvents = [];
    touch = false;
  }

  function onWheel(e) {
    rect = plot.getBoundingClientRect();
    const isInRect = inRect(rect, e.x, e.y);

    let axis = findAxis(e.x, e.y);
    
    if (isInRect || axis.scale === "x") {
        const factor = 0.7;

        let left = uplot.cursor.left;
        let leftPct = isInRect ? left / rect.width : 0.5;
        let oxRange = uplot.scales.x.max - uplot.scales.x.min;
        
        let xVal = isInRect ? uplot.posToVal(left, "x") : oxRange / 2 + uplot.scales.x.min;
        
        let nxRange = e.deltaY < 0 ? oxRange * factor : oxRange / factor;
        let nxMin = xVal - leftPct * nxRange;
        let nxMax = nxMin + nxRange;
        [nxMin, nxMax] = clamp(nxRange, nxMin, nxMax, xRange, xMin, xMax);
        const range = { min: nxMin, max: nxMax };
        uplot.setScale("x", range);
      } else if (axis) {
        let scale = uplot.scales[axis.scale];
        const min = 0;
        const delta = 5;
        const max = Math.max(min, uplot.scales[axis.scale].max + (delta * -e.deltaY / 100));
        uplot.setScale(axis.scale, { min: 0, max });
      }
  }

  function findAxis(x, y) {
    for (let axis of uplot.axes) {
      let axisRect = axis._el.getBoundingClientRect();

      // 0: top, 1: right, 2: bottom, 3: left 
      if (axis.side === 0) axisRect = expandRect(axisRect, 0, 0, axis.labelSize, 0);
      else if (axis.side === 1) axisRect = expandRect(axisRect, 0, axis.labelSize, 0, 0);
      else if (axis.side === 2) axisRect = expandRect(axisRect, 0, 0, 0, axis.labelSize);
      else if (axis.side === 3) axisRect = expandRect(axisRect, axis.labelSize, 0, 0, 0);

      if (inRect(axisRect, x, y)) {
        return axis;
      }
    }
    return null;
  }

  let range;
  function zoom() {
    rafPending = false;

    let xFactor = from.d / to.d;
    let leftPct = to.x/rect.width;

    let nxRange = oxRange * xFactor;
    let nxMin = xVal - leftPct * nxRange;
    let nxMax = nxMin + nxRange;

    range = { min: Math.max(xMin, nxMin), max: Math.min(nxMax, xMax) };
    uplot.setScale("x", range);
  }

  function storePos(t) {
    let t0 = pointerEvents[0];
    let t0x = t0.clientX - rect.left;

    if (pointerEvents.length == 1) {
      t.x = t0x;
      t.d = t.dx = 1;
    }
    else {
      let t1 = pointerEvents[1];
      let t1x = t1.clientX - rect.left;

      let xMin = Math.min(t0x, t1x);
      let xMax = Math.max(t0x, t1x);

      // midpts
      t.x = (xMin+xMax)/2;
      t.dx = xMax - xMin;

      // dist
      t.d = Math.sqrt(t.dx * t.dx);
    }
  }

  function clamp(nRange, nMin, nMax, fRange, fMin, fMax) {
    if (nRange > fRange) {
      nMin = fMin;
      nMax = fMax;
    } else if (nMin < fMin) {
      nMin = fMin;
      nMax = fMin + nRange;
    } else if (nMax > fMax) {
      nMax = fMax;
      nMin = fMax - nRange;
    }

    return [nMin, nMax];
  }

  let throttlePause = false;
  function throttle(callback, time) {
    if (throttlePause) return;
    throttlePause = true;
    setTimeout(() => {
      callback();
      throttlePause = false;
    }, time);
  }

  onMount(() => {
    uplot = createChart(chart, title);
    plot = uplot.root.querySelector(".u-over");

    setTimeout(() => {
      onResize();
    }, 100);
  });

  function onResize() {
    const width = window.innerWidth - 10;
    const height = window.innerHeight - 10;
    uplot.setSize({ width, height });
  }
</script>

<svelte:window on:resize|passive={onResize} />
<div>
  <div bind:this={chart} class="chart" class:border class:canPan class:isPanning role="graphics-object"
    on:wheel|preventDefault={(e) => throttle(() => onWheel(e), 1)}
    on:pointerdown={onPointerDown}
    on:pointermove|preventDefault={(e) => throttle(() => onPointerMove(e), 1)}
    on:pointerup={onPointerUp}
    on:dblclick|preventDefault={onDblClick}>
  </div>
</div>

<style>
  :global(.uplot) {
    touch-action: none;
  }

  :global(.border .u-under) {
    border: 1px solid #ddd;
  }

  :global(.u-legend) {
    margin-top: -25px;
  }

  :global(.u-under) {
    background-color: white;
  }

  :global(.u-axis) {
    position: absolute; /* Need for DOM elements to give correct getBoundingClientRect, for some reason */
  }

  .chart.canPan {
    cursor: grab;
  }

  .chart.isPanning {
    cursor: grabbing;
  }
</style>